import {
  AppError,
  isMultipartBody,
  type FileRef,
  type HttpRequest,
  type HttpResponse,
  type HttpTransport,
  type UploadProgress,
} from '@sunucupark/client-core';

/** Turns the core's declarative multipart body into the browser's `FormData`. */
function formDataOf(parts: Array<{ name: string; value: string | FileRef }>): FormData {
  const form = new FormData();
  for (const part of parts) {
    if (typeof part.value === 'string') {
      form.append(part.name, part.value);
      continue;
    }
    const handle = part.value.handle;
    if (!(handle instanceof Blob))
      throw new TypeError(`Part "${part.name}" carries no file the browser can send`);
    form.append(part.name, handle, part.value.name);
  }
  return form;
}

/**
 * The browser's `fetch` behind the core's transport port. The only place in the
 * webmail that talks to the network directly (ADR 0006).
 */
export class FetchTransport implements HttpTransport {
  constructor(private readonly fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis)) {}

  async send(request: HttpRequest): Promise<HttpResponse> {
    const body = isMultipartBody(request.body) ? formDataOf([...request.body.parts]) : request.body;
    const response = await this.fetchImpl(request.url, {
      method: request.method,
      headers: request.headers,
      body,
      signal: request.signal,
      credentials: request.credentials ?? 'omit',
      // Answers are never cached by the browser: the query cache is ours to manage.
      cache: 'no-store',
      redirect: 'error',
    });
    return {
      status: response.status,
      ok: response.ok,
      headers: { get: (name) => response.headers.get(name) },
      text: () => response.text(),
    };
  }

  /**
   * The same request through `XMLHttpRequest`, which is still the only browser
   * API that says how much of a body has gone out — `fetch` reports nothing
   * until the answer arrives, and request streaming needs HTTP/2 and a duplex
   * flag Safari does not have. Only attachment uploads take this path; every
   * other request goes through `fetch` above.
   */
  sendWithProgress(
    request: HttpRequest,
    onProgress: (progress: UploadProgress) => void,
  ): Promise<HttpResponse> {
    const body = isMultipartBody(request.body) ? formDataOf([...request.body.parts]) : request.body;
    return new Promise<HttpResponse>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(request.method, request.url, true);
      xhr.withCredentials = (request.credentials ?? 'omit') === 'include';
      for (const [name, value] of Object.entries(request.headers)) xhr.setRequestHeader(name, value);

      xhr.upload.addEventListener('progress', (event) => {
        onProgress({ loaded: event.loaded, total: event.lengthComputable ? event.total : null });
      });
      xhr.addEventListener('load', () => {
        resolve({
          status: xhr.status,
          ok: xhr.status >= 200 && xhr.status < 300,
          headers: { get: (name) => xhr.getResponseHeader(name) },
          text: () => Promise.resolve(xhr.responseText),
        });
      });
      xhr.addEventListener('error', () => {
        reject(new AppError('NETWORK', `The upload to ${request.url} failed`));
      });
      xhr.addEventListener('abort', () => {
        reject(new AppError('ABORTED', `The upload to ${request.url} was cancelled`));
      });

      const abort = () => {
        xhr.abort();
      };
      if (request.signal?.aborted) {
        abort();
        return;
      }
      request.signal?.addEventListener('abort', abort, { once: true });
      xhr.addEventListener('loadend', () => {
        request.signal?.removeEventListener('abort', abort);
      });

      xhr.send(body ?? null);
    });
  }
}
